import React, { useEffect, useState, useMemo } from "react";
import { PageHeader, Modal, Toast, EmptyState } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { groupOrgUnitsByParent } from "@/lib/orgTree";
import Select from "@/components/Select";
import { lock } from "@/lib/perm";
import useFilters from "@/lib/useFilters";
import { FilterForm } from "@/components/FilterActions";

import RowActions from "@/components/RowActions";
function formatSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function daysLeft(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

export default function EmployeeFiles() {
  const { permissionsReady, has } = useAuth();
  // vederea administrativă (toate dosarele) = Admin/HR sau oricine are „Vizualizează dosarele tuturor angajaților” prin subrol
  // Vederea administrativă (dosarele altora) = dreptul „Vizualizează dosarele angajaților” (din profilul rolului HR/Admin sau
  // dintr-un profil de acces alocat); serverul îl verifică pe fiecare raport de muncă (structură/categorie).
  const isAdminHr = has("documents.view_all");
  const [mode, setMode] = useState("edit");   // "view" = doar citire (fără încărcare/ștergere), „edit” = cu butoanele de scriere

  const [employments, setEmployments] = useState([]);
  const [units, setUnits] = useState([]);
  const [myFiles, setMyFiles] = useState(null);
  // filtre cu „Aplică filtre”; implicit se afișează toate rapoartele de muncă
  const ff = useFilters({ unit: "", last: "", first: "", marca: "" });
  const fUnitId = ff.applied.unit, fLastName = ff.applied.last, fFirstName = ff.applied.first, fMarca = ff.applied.marca;

  const [selected, setSelected] = useState(null); // employment aleasă (dosarul curent)
  const [categories, setCategories] = useState([]);
  const [typesByCategory, setTypesByCategory] = useState({});
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "info" });

  useEffect(() => {
    if (!isAdminHr && !permissionsReady) return;   // încă nu știm dacă are vedere administrativă (prin subrol)
    if (isAdminHr) {
      Promise.all([api.get("/mdm/employments"), api.get("/mdm/org-units")])
        .then(([e, u]) => { setEmployments(e.data); setUnits(u.data); });
    } else {
      api.get("/employee-files/my-files").then(r => {
        setMyFiles(r.data);
        if (r.data.length === 1) selectEmployment(r.data[0]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminHr, permissionsReady]);

  const selectEmployment = async (empl, m = "edit") => {
    setMode(m);
    setSelected(empl);
    try {
    const [c, docs] = await Promise.all([
      api.get("/employee-files/categories", { params: { active_only: true } }),
      api.get("/employee-files/documents", { params: { employment_id: empl.id } }),
    ]);
    const cats = [...c.data].sort((a, b) => a.order_index - b.order_index);
    setCategories(cats);
    setDocuments(docs.data);
    const entries = await Promise.all(cats.map(async cat => {
      const t = await api.get(`/employee-files/categories/${cat.id}/types`, { params: { active_only: true } });
      return [cat.id, [...t.data].sort((a, b) => a.order_index - b.order_index)];
    }));
    setTypesByCategory(Object.fromEntries(entries));
      } catch (e) {
      setMsg({ text: "Nu s-au putut încărca categoriile sau documentele dosarului: " + (e?.response?.data?.detail || "eroare de rețea"), type: "error" });
    }
  };

  const reloadDocuments = async () => {
    const docs = await api.get("/employee-files/documents", { params: { employment_id: selected.id } });
    setDocuments(docs.data);
  };

  const back = () => { setSelected(null); setDocuments([]); setCategories([]); };

  const submitUpload = async () => {
    if (!uploading.file) return setMsg({ text: "Selectează un fișier", type: "error" });
    if (uploading.has_expiry && (!uploading.expiry_start || !uploading.expiry_end))
      return setMsg({ text: "Completează perioada de valabilitate", type: "error" });
    const fd = new FormData();
    fd.append("employment_id", selected.id);
    fd.append("category_id", uploading.category_id);
    fd.append("document_type_id", uploading.document_type_id);
    fd.append("has_expiry", uploading.has_expiry ? "true" : "false");
    if (uploading.has_expiry) {
      fd.append("expiry_start", uploading.expiry_start);
      fd.append("expiry_end", uploading.expiry_end);
    }
    fd.append("file", uploading.file);
    try {
      await api.post("/employee-files/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploading(null); reloadDocuments();
      setMsg({ text: "Document încărcat", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare la încărcare", type: "error" }); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Ștergi „${doc.original_filename}"?`)) return;
    try {
      await api.delete(`/employee-files/documents/${doc.id}`);
      reloadDocuments();
      setMsg({ text: "Document șters", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  // Descărcare și vizualizare: cerere autentificată din pagină (nu o filă nouă către /api/...), ca să meargă la fel pe
  // calculator și pe telefon, iar o eroare a serverului să apară ca mesaj, nu ca pagină goală.
  const docError = async (e) => {
    let detail = e?.response?.data?.detail;
    if (!detail && e?.response?.data instanceof Blob) {
      try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* răspuns fără detaliu */ }
    }
    setMsg({ text: detail || "Documentul nu a putut fi deschis", type: "error" });
  };
  const fetchDoc = async (doc, inline) => {
    const r = await api.get(`/employee-files/documents/${doc.id}/download`, { params: inline ? { inline: true } : {}, responseType: "blob" });
    return new Blob([r.data], { type: doc.content_type || r.data.type || "application/octet-stream" });
  };
  const downloadDoc = async (doc) => {
    try {
      const url = URL.createObjectURL(await fetchDoc(doc, false));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { docError(e); }
  };
  const canView = (doc) => /^(application\/pdf|image\/)/.test(doc.content_type || "") || /\.(pdf|jpe?g|png)$/i.test(doc.original_filename || "");
  const viewDoc = async (doc) => {
    const w = window.open("", "_blank");   // se deschide pe click, ca blocarea ferestrelor să nu o oprească
    try {
      const url = URL.createObjectURL(await fetchDoc(doc, true));
      if (w) w.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60000);
    } catch (e) { if (w) w.close(); docError(e); }
  };

  // Arbore organizatoric — id-ul copiilor direcți ai fiecărei structuri, ca
  // să putem colecta întregul subarbore al structurii selectate din click.
  const childrenOf = useMemo(() => {
    const m = {};
    units.forEach(u => { if (u.parent_id) (m[u.parent_id] = m[u.parent_id] || []).push(u.id); });
    return m;
  }, [units]);
  const byParent = useMemo(() => groupOrgUnitsByParent(units), [units]);

  // Opțiunile filtrului de structură, în ordinea ierarhiei (indentate după nivel)
  const unitOptions = useMemo(() => {
    const out = [];
    const walk = (parentId, depth) => (byParent[parentId] || []).filter(u => u.active)
      .sort((a, b) => (a.code || "").localeCompare(b.code || "")).forEach(u => {
        out.push({ id: u.id, label: `${"\u00A0\u00A0".repeat(depth)}${u.code ? u.code + " · " : ""}${u.name}` });
        walk(u.id, depth + 1);
      });
    walk("root", 0);
    return out;
  }, [byParent]);

  const subtreeUnitIds = (rootId) => {
    const out = new Set([rootId]);
    const walk = (id) => (childrenOf[id] || []).forEach(cid => { if (!out.has(cid)) { out.add(cid); walk(cid); } });
    walk(rootId);
    return out;
  };

  const selectedSubtree = fUnitId ? subtreeUnitIds(fUnitId) : null;
  const selectedUnit = fUnitId ? units.find(u => u.id === fUnitId) : null;

  // Filtrele se combină (ȘI): structura aleasă include și subunitățile ei.
  const filteredEmployments = employments.filter(e =>
    e.status !== "terminated"
    && (!selectedSubtree || selectedSubtree.has(e.org_unit_id))
    && (!fMarca || e.marca?.toLowerCase().includes(fMarca.toLowerCase()))
    && (!fLastName || (e.last_name || "").toLowerCase().includes(fLastName.toLowerCase()))
    && (!fFirstName || (e.first_name || "").toLowerCase().includes(fFirstName.toLowerCase()))
  );
  const showEmploymentList = true;

  const canBrowseBack = isAdminHr || (myFiles && myFiles.length > 1);

  return (
    <>
      <PageHeader title="Dosar Electronic Angajat"
        subtitle={selected ? `Marca ${selected.marca} — ${selected.user_name || ""}` : "Documentele fiecărui raport de muncă"} />
      <Toast msg={msg.text} type={msg.type} />

      {!selected && isAdminHr && (
        <>
          <FilterForm f={ff} testid="ef-filters" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
              <div><label className="label">Nume</label>
                <input className="input" value={ff.draft.last} onChange={e => ff.set({ last: e.target.value })} data-testid="ef-filter-lastname" placeholder="ex: Popescu" /></div>
              <div><label className="label">Prenume</label>
                <input className="input" value={ff.draft.first} onChange={e => ff.set({ first: e.target.value })} data-testid="ef-filter-firstname" placeholder="ex: Ana" /></div>
              <div><label className="label">Marcă</label>
                <input className="input" value={ff.draft.marca} onChange={e => ff.set({ marca: e.target.value })} data-testid="ef-filter-marca" /></div>
              <div><label className="label">Structură organizatorică</label>
                <Select className="select" value={ff.draft.unit || ""} onChange={e => ff.set({ unit: e.target.value || "" })} data-testid="ef-filter-unit">
                  <option value="">Toate structurile</option>
                  {unitOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </Select></div>
            </div>
            {ff.draft.unit && <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>Structura aleasă include și subunitățile ei.</div>}
          </FilterForm>

          {showEmploymentList && (
            <div className="card">
              {selectedUnit && (
                <div style={{ padding: "10px 16px", borderBottom: "1px solid #E2E8F0", fontSize: 13, color: "#334155" }}>
                  Angajați din <b>{selectedUnit.name}</b> și substructurile ei
                </div>
              )}
              <table className="tbl">
                <thead><tr><th>Marcă</th><th>Nume și prenume</th><th>Structură</th><th></th></tr></thead>
                <tbody>{filteredEmployments.map(e => {
                  const u = units.find(x => x.id === e.org_unit_id);
                  return (
                    <tr key={e.id} data-testid={`ef-row-${e.marca}`}>
                      <td style={{ fontWeight: 600 }} className="tabular-nums">{e.marca}</td>
                      <td>{e.user_name || "—"}</td>
                      <td>{u?.name || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <RowActions testid={`row-menu-${e.marca}`} items={[
                          { label: "Vizualizează", icon: "eye", onClick: () => selectEmployment(e, "view"), testid: `ef-view-${e.marca}` },
                          { label: "Editează dosarul", icon: "edit", onClick: () => selectEmployment(e, "edit"), testid: `ef-open-${e.marca}`, ...lock(has("documents.upload") || has("documents.delete")) },
                        ]} />
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
              {filteredEmployments.length === 0 && <EmptyState text="Niciun raport de muncă găsit." />}
            </div>
          )}
        </>
      )}

      {!selected && !isAdminHr && myFiles && myFiles.length > 1 && (
        <div className="card">
          <table className="tbl">
            <thead><tr><th>Marcă</th><th>Funcție</th><th>Documente</th><th></th></tr></thead>
            <tbody>{myFiles.map(e => (
              <tr key={e.id} data-testid={`ef-my-row-${e.marca}`}>
                <td style={{ fontWeight: 600 }} className="tabular-nums">{e.marca}</td>
                <td>{e.position_title || "—"}</td>
                <td className="tabular-nums">{e.document_count}</td>
                <td><button className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => selectEmployment(e)}>Deschide dosarul</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {!selected && !isAdminHr && myFiles && myFiles.length === 0 && (
        <EmptyState text="Nu ai niciun raport de muncă asociat." />
      )}

      {selected && (
        <>
          {canBrowseBack && <button className="btn btn-secondary" style={{ marginBottom: 14 }} onClick={back} data-testid="ef-back">← Înapoi</button>}
          {isAdminHr && mode === "view" && <div className="view-only-note" data-testid="ef-view-note">Vizualizare — documentele se pot citi și descărca, dar nu se pot încărca sau șterge. Pentru modificări, deschide dosarul cu „Editează dosarul”.</div>}
          <div style={{ display: "grid", gap: 16 }}>
            {categories.map(cat => (
              <div key={cat.id} className="card fade-up" style={{ padding: 20 }} data-testid={`ef-category-${cat.name}`}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>{cat.name}</div>
                <div style={{ display: "grid", gap: 14 }}>
                  {(typesByCategory[cat.id] || []).map(t => {
                    const files = documents.filter(d => d.document_type_id === t.id);
                    return (
                      <div key={t.id}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>{t.name}</div>
                          {isAdminHr && mode === "edit" && (
                            <button className="btn btn-secondary" style={{ padding: "2px 8px", fontSize: 11 }} {...lock(has("documents.upload"))}
                              onClick={() => setUploading({ category_id: cat.id, document_type_id: t.id, has_expiry: false, expiry_start: "", expiry_end: "", file: null })}
                              data-testid={`ef-upload-${t.name}`}>+ Încarcă</button>
                          )}
                        </div>
                        <div style={{ display: "grid", gap: 6 }}>
                          {files.map(d => {
                            const dl = d.has_expiry ? daysLeft(d.expiry_end) : null;
                            return (
                              <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#F8FAFC", borderRadius: 6 }} data-testid={`ef-doc-${d.original_filename}`}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600 }}>{d.original_filename}</div>
                                  <div style={{ fontSize: 11, color: "#64748B" }}>
                                    {formatSize(d.size_bytes)}
                                    {d.has_expiry && (
                                      <span style={{ marginLeft: 8, color: dl !== null && dl <= 30 ? "#DC2626" : "#64748B", fontWeight: dl !== null && dl <= 30 ? 700 : 400 }}>
                                        · Expiră: {d.expiry_end}{dl !== null ? ` (${dl}z)` : ""}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {canView(d) && <button className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => viewDoc(d)} data-testid={`ef-view-doc-${d.original_filename}`}>Vizualizează documentul</button>}
                                <button className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => downloadDoc(d)} data-testid={`ef-download-${d.original_filename}`}>Descarcă</button>
                                {isAdminHr && mode === "edit" && <button className="btn btn-danger" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => deleteDoc(d)} data-testid={`ef-delete-${d.original_filename}`} {...lock(has("documents.delete"))}>Șterge</button>}
                              </div>
                            );
                          })}
                          {files.length === 0 && <div style={{ fontSize: 12, color: "#94A3B8" }}>Niciun document.</div>}
                        </div>
                      </div>
                    );
                  })}
                  {(typesByCategory[cat.id] || []).length === 0 && <div style={{ fontSize: 12, color: "#94A3B8" }}>Nicio subcategorie definită.</div>}
                </div>
              </div>
            ))}
            {categories.length === 0 && <EmptyState text="Nicio categorie de documente configurată." />}
          </div>
        </>
      )}

      {uploading && (
        <Modal title="Încarcă document" onClose={() => setUploading(null)}>
          <div style={{ display: "grid", gap: 12 }}>
            <div><label className="label label-req">Fișier</label>
              <input type="file" onChange={e => setUploading({ ...uploading, file: e.target.files?.[0] || null })} data-testid="ef-upload-file" /></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" id="ef-has-expiry" checked={uploading.has_expiry}
                onChange={e => setUploading({ ...uploading, has_expiry: e.target.checked })} data-testid="ef-has-expiry" />
              <label htmlFor="ef-has-expiry" style={{ fontSize: 13, cursor: "pointer" }}>Are termen de valabilitate</label>
            </div>
            {uploading.has_expiry && (
              <div className="grid-2">
                <div><label className="label label-req">Data început</label>
                  <input className="input" type="date" value={uploading.expiry_start} onChange={e => setUploading({ ...uploading, expiry_start: e.target.value })} data-testid="ef-expiry-start" /></div>
                <div><label className="label label-req">Data sfârșit</label>
                  <input className="input" type="date" value={uploading.expiry_end} onChange={e => setUploading({ ...uploading, expiry_end: e.target.value })} data-testid="ef-expiry-end" /></div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setUploading(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={submitUpload} data-testid="ef-submit-upload" {...lock(has("documents.upload"))}>Încarcă</button>
          </div>
        </Modal>
      )}

    </>
  );
}
